import { styles } from './CarouselLeadImage.css';
import React, { ReactNode, useRef } from 'react';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { ProductCarousel, ProductCarouselProps } from '../ProductCarousel/ProductCarousel';
import { GoToLink, GoToLinkProps } from '../GoToLink/GoToLink';
import { Icon, IconName } from '@jbhi-fi/lanyard-ui/components/Icon';
import { formatDate } from '../ProductHtmlBanner/utils';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import AspectRatio from '@jbhi-fi/lanyard-ui/components/AspectRatio';
import { ContentfulYouTube } from '@jbhi-fi/apis/src/contentful/types';
import { YouTubeTransformer } from '../Contentful/transformers/YouTubeTransformer';
import { ASPECT_RATIOS, YouTubePlayer } from '../YouTubePlayer/YouTubePlayer';

export type CarouselLeadImageProps = {
  desktopImage?: ReactNode;
  mobileImage?: ReactNode;
  linkProps?: GoToLinkProps;
  ctaButton?: ReactNode;
  heading: string;
  subText?: string;
  isWhiteSubText?: boolean;
  productCarouselProps: ProductCarouselProps;
  shouldHideHeading?: boolean;
  brandLogo?: IconName;
  endDate?: string;
  video?: ContentfulYouTube;
};

export const CarouselLeadImage = ({
  desktopImage,
  mobileImage,
  linkProps,
  ctaButton,
  heading,
  subText,
  isWhiteSubText,
  productCarouselProps,
  brandLogo,
  endDate,
  video,
  shouldHideHeading = false,
}: CarouselLeadImageProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isDesktop = useBreakpoint('md', 'up');
  const isDesktopView = useBreakpoint('md', 'up', ref);
  const hasVideo = !!video;
  const transformedYouTubeProps: Partial<ReturnType<typeof YouTubeTransformer.toYouTubePlayerProps>> = video
    ? YouTubeTransformer.toYouTubePlayerProps(video)
    : {};
  const { landscapeThumbnailUrl, portraitThumbnailUrl, ...youTubeProps } = transformedYouTubeProps;

  const hasRelevantImage = isDesktopView ? !!desktopImage : !!mobileImage;
  const hasLeadVisual = hasVideo || hasRelevantImage || !!brandLogo;
  const hasDesktopLinkProps = isDesktopView && linkProps;
  const shouldHideEmptyMobileLead =
    shouldHideHeading && !isDesktopView && !hasRelevantImage && !brandLogo && !subText && !endDate && !hasVideo;

  return (
    <div className={styles.wrapper} ref={ref}>
      {hasDesktopLinkProps && hasLeadVisual ? <GoToLink {...linkProps} className={styles.link} /> : null}
      <div
        className={styles.container({ hasImage: hasRelevantImage || hasVideo, hasBrandLogo: !hasVideo && !!brandLogo })}
      >
        {hasRelevantImage && !hasVideo ? (
          <div className={styles.imageContainer}>{isDesktopView ? desktopImage : mobileImage}</div>
        ) : null}
        {!shouldHideEmptyMobileLead ? (
          <div
            className={styles.leadContainer({
              hasImage: hasRelevantImage || hasVideo,
              hasBrandLogo: !hasVideo && !!brandLogo,
              hasVideo,
            })}
          >
            {hasVideo ? (
              <div className={styles.videoContainer}>
                <AspectRatio ratio={isDesktop ? ASPECT_RATIOS.portrait : ASPECT_RATIOS.landscape}>
                  <YouTubePlayer
                    {...youTubeProps}
                    orientation={isDesktop ? 'Portrait' : 'Landscape'}
                    landscapeThumbnailUrl={landscapeThumbnailUrl}
                    portraitThumbnailUrl={portraitThumbnailUrl}
                  />
                </AspectRatio>
              </div>
            ) : (
              <>
                {brandLogo ? (
                  <Icon name={brandLogo} className={styles.brandLogo} preserveAspectRatio="xMinYMid meet" />
                ) : null}
                <div className={styles.headingRow}>
                  {!!heading && (!shouldHideHeading || hasRelevantImage) ? (
                    <CalloutFont as="h1" className={styles.headingText({ hasImage: hasRelevantImage && !hasVideo })}>
                      {heading}
                    </CalloutFont>
                  ) : null}
                  {hasDesktopLinkProps && !hasLeadVisual ? (
                    <GoToLink {...linkProps} className={styles.inlineLink} />
                  ) : null}
                </div>
                {endDate ? (
                  <CalloutFont as="span" className={styles.endDate}>
                    ENDS {formatDate(new Date(endDate))}
                  </CalloutFont>
                ) : null}
                {subText ? <p className={styles.subText({ isWhiteSubText })}>{subText}</p> : null}
              </>
            )}
            {isDesktopView && ctaButton && hasLeadVisual && !hasVideo ? (
              <div className={styles.ctaButton}>{ctaButton}</div>
            ) : null}
          </div>
        ) : null}
        <div className={styles.carouselContainer}>
          <ProductCarousel {...productCarouselProps} />
        </div>
      </div>
      {ctaButton && (!isDesktopView || !hasLeadVisual || hasVideo) ? (
        <div className={styles.bottomCtaButton}>{ctaButton}</div>
      ) : null}
    </div>
  );
};
